// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import alert from "../../../../../share/alert";
import loading from "../../../../../share/components/vue/ProcessingIcon";
import fullModal from "../../../../../share/components/vue/FullModal";
import noTextSVGButton, { ICON_KEY, BUTTON_TYPE } from "../../../../../share/components/vue/NoTextSVGButton";
import WrappedTeleport from "../../../../../share/components/vue/WrappedTeleport";
import customProgress from "../../../../../share/components/vue/CustomProgress";
import memoPage from "../../../../vue/MemoPage";
import dataMonitor from "../../../../vue/obd/individual/dataMonitor/ObdDataMonitor";
import apiService from "../../../../../share/util/api/service";
import dmUtil, { TAB } from "../../../dataMonitor/dataMonitorUtil";
import { createZoomTabHandlersForOptions, isResetZoomDisabled } from '../../../dataMonitor/dataMonitorZoomUtil';
import { CsvFileWriter, getMeasurementFileName } from '../../../dataMonitor/dataMonitorFileUtil';
import { exportCsv } from '../../../dataRecollection/obdDataMonitor/csv';
import diagnoResource from '../../../../../share/util/diagnose';

export default {
  name: "obdIndividualDataMonitor",
  data() {
    return {
      processing: false,
      showCsvProgress: false, // CSVエクスポートの進捗表示フラグ
      currentWizard: "",
      // システム間移動判定（OBD の場合は使用しないが、undefined のままだと診断画面の初期化が走らないため false としている）
      isUseCache: false,
      // 項目選択画面表示フラグ
      showSelect: true,
      // 計測中フラグ
      inExecution: false,
      // 計測データ再生中フラグ
      inPlaying: false,
      destinationAPI: apiService.OBD,
      navItemLabel: "individual.individual_diagnosis_function_data_monitor",
      obdFunctionLabelKey: "fixed_resource.obd_menu",
      measureId: "", // 計測ID、計測時に取得
      isSpecial: false,
      isObd: true,
      modalMemo: false,
      checkLines: 0,  // グラフ項目件数
      currentTab: TAB.LIST,
      isZoomPanActive: false,
      isMemoDialogLoading: false, // memo画面でAPIと通信しているかのフラグ
      dataMonitorFunctionSettings: new dmUtil.DataMonitorFunctionSettings(dmUtil.OBD), // DM機能の設定インスタンス
      // 全画面モーダルの表示有無
      showModal: false,
      // Measureコンポーネントの描画完了したか否か。Measureコンポーネントへのteleportを描画完了まで待つ必要があるため、チェックする。
      measureMounted: false,
      // SVGアイコンの種類
      ICON_KEY: ICON_KEY,
      // SVGアイコンのボタンの種類
      BUTTON_TYPE: BUTTON_TYPE,
      exportPercentage: 0, // CSVエクスポートの進捗率
    };
  },

  props: {
    // noop
  },

  components: {
    "processing-view": loading,
    "data-monitor": dataMonitor,
    "memo-page": memoPage,
    "full-modal": fullModal,
    "no-text-svg-button": noTextSVGButton,
    "wrapped-teleport": WrappedTeleport,
    "custom-progress": customProgress,
  },

  watch: {
    language: {
      immediate: true,
      handler() { },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      vehicleInfo: (state) => state.vehicle.vehicleInfo,
      obdSystems: (state) => state.obd.systems,
    }),

    btnDisabled() {
      const disabled = {
        back: this.inExecution || this.inPlaying,
        execute: this.inPlaying,
        memo: this.inExecution || !this.dataMonitorFunctionSettings.isPersist,
        graph: !this.checkLines || this.isZoomPanActive,
        resetZoom: isResetZoomDisabled({
          inExecution: this.inExecution,
          inPlaying: this.inPlaying,
          hasCheckLines: Boolean(this.checkLines),
          currentTab: this.currentTab,
        }),
        export: this.inExecution,
      };
      return disabled;
    },

    projectId() {
      return this.vehicleInfo?.vehicle?.project.id || '';
    },
  },

  /** 画面遷移時、計測中か判定 */
  beforeRouteLeave(to, from, next) {
    if ((to.name === "login") && (to.fullPath === "/") && (to.path === "/")) {
      this.$refs.data_monitor.setSignout();
      next();
      return;
    }
    if (to.name === "changeUnit") {
      if (!this.showSelect) {
        // 単位設定は移動不可
        this.changeUnitAlert();
        return;
      }
    }
    // 計測中は移動不可
    if (this.inExecution) {
      this.inExecutionAlert();
    } else {
      this.showSelect = true;
      next();
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),
    ...createZoomTabHandlersForOptions({
      isResetZoomDisabled() {
        return this.btnDisabled.resetZoom;
      },
    }),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
      this.inExecution = false;
      this.showSelect = true;
      // パスに"menu"を含まない場合は永続化しない
      if (!this.$route.path.includes('menu')) {
        this.dataMonitorFunctionSettings.isPersist = false;
      }
    },

    /** ウィザードを閉じた後の処理 */
    closeWizard() {
      this.currentWizard = "";
    },

    /** 項目選択/計測機能切替 */
    toggleShowSelect(flg) {
      if (flg === undefined || flg === null) {
        this.showSelect = !this.showSelect;
      } else {
        this.showSelect = flg;
      }
    },

    /** 計測開始/停止状態切替 */
    toggleExecute(flg) {
      if (flg === undefined || flg === null) {
        this.inExecution = !this.inExecution;
      } else {
        this.inExecution = flg;
      }
    },

    /** 計測データ再生/停止状態切替 */
    togglePlaying(flg) {
      if (flg === undefined || flg === null) {
        this.inPlaying = !this.inPlaying;
      } else {
        this.inPlaying = flg;
      }
      if (this.inPlaying) {
        this.isZoomPanActive = false;
      }
    },

    /** モニタ開始 */
    startMonitor() {
      this.$refs.data_monitor.startMonitor();
    },

    /** モニタ停止 */
    stopMonitor() {
      this.$refs.data_monitor.stopMonitor();
    },

    /** 項目選択に戻る */
    backSelect() {
      // 計測中は移動不可
      if (this.inExecution) {
        this.inExecutionAlert();
      }
      else {
        // 初期化
        this.checkLines = 0;
        this.toggleShowSelect(true);
      }
    },

    /** 計測IDを変更 */
    changeMeasureId(id) {
      this.measureId = id;
    },

    /** グラフ項目件数を変更 */
    changeCheckValue(checkLines) {
      this.checkLines = checkLines;
    },

    /**
     * ズーム/パン状態を更新
     * @param {boolean} isActive
     */
    zoomPanStateChanged(isActive) {
      this.isZoomPanActive = isActive;
    },

    /** メモを開く */
    openMemo() {
      this.modalMemo = true;
    },
    // メモ情報の保存
    async memoSave() {
      await this.$refs.memo.save();
    },
    // メモ情報のクリア
    memoClear: function () {
      this.$refs.memo.clear();
    },
    // メモを閉じる
    closeNoteModal() {
      this.modalMemo = false;
    },

    // グラフ設定
    handleConfigureGraph() {
      this.$refs.data_monitor.handleConfigureGraph();
    },

    /** 計測中警告ダイアログ */
    inExecutionAlert() {
      alert.showWarning(
        this,
        null,
        this.$t("individual.alert_data_monitor_measurement"),
        () => { }
      );
    },

    /** 単位設定警告ダイアログ */
    changeUnitAlert() {
      const label = "individual.alert_data_monitor_change_unitsetting";
      alert.showWarning(this, null, this.$t(label), () => { });
    },

    /**
     * 最大化ボタン押下処理
     */
    maximum() {
      this.showModal = true;
    },

    /**
     * 最小化ボタン押下処理
     */
    minimum() {
      this.showModal = false;
    },

    /**
     * 計測画面全体をteleportさせるモーダルダイアログのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     */
    modalContentId() {
      return `full-modal-content-obd-${this.dataMonitorFunctionSettings.typeCaller}`;
    },

    /**
     * グラフ設定ボタンなどをteleportさせるモーダルダイアログのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     * @returns {string} 作成したID
     */
    graphSettingsDestinationId() {
      return `graph-settings-destination-obd-${this.dataMonitorFunctionSettings.typeCaller}`;
    },

    /**
     * 子コンポーネントからObdDataMonitorMeasureのMount・Unmountの通知を受け取る
     * @param {boolean} isMounted Mountされたか否か
     */
    measureMountedEvent(isMounted) {
      this.measureMounted = isMounted;
      if (!isMounted) {
        // 計測画面のコンポーネントが破棄されたら、計測画面を離れたと判断して全画面モーダルも閉じる
        this.showModal = false;
      }
    },

    /**
     * CSVエクスポートの進捗更新
     * @param {number} allCount 全体数
     * @param {number} currentCount 現在の数
     */
    updateExportProgress(allCount, currentCount) {
      this.exportPercentage = dmUtil.calculatePercentage(allCount, currentCount);
    },

    /**
     * ファイルエクスポートボタン押下時の処理
     */
    async csvExport() {
      let csvWriter = null;
      try {
        this.showCsvProgress = true;
        this.exportPercentage = 0;
        const measurementId = this.$refs.data_monitor.getMeasurementId();

        let note = null;
        let dataName = null;
        let vehicleInfo = {
          information: {}
        };
        if (this.dataMonitorFunctionSettings.isPersist) {
          // 計測データを永続化する場合はproject情報を取得
          const endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id + '/' + this.measureId;
          const res = await apiService.getCall(this.destinationAPI, endPoint, diagnoResource.operationFrom(this.$router.currentRoute.value.name));
          dataName = res.data.project.measured.data_name;
          note = res.data.project.measured.memo;
          vehicleInfo = {
            // 車両情報を設定
            ...this.vehicleInfo.vehicle.vin,
            information: {
              ...this.vehicleInfo.vehicle.spec,
              vin: this.vehicleInfo.vehicle.vin.vin,
              vehicle_name: this.vehicleInfo.vehicle.spec.model
            }
          };
        }

        // エクスポートに必要な情報に整形
        const projectDetails = {
          ...vehicleInfo,
          measures: [
            {
              [apiService.OBD]: [
                {
                  measure_id: measurementId,
                  name: dataName,
                  note: note
                }
              ]
            }
          ]
        };

        const currentSystemNo = this.$route.params.systemNo;
        const currentSystem = (currentSystemNo && this.obdSystems?.length) ? this.obdSystems.find(system => system.systemNo === currentSystemNo) : null;
        const ecuName = currentSystem ? currentSystem.systemName : null;

        const functionName = `${this.$t('fixed_resource.obd_menu_datamonitor')}${this.$t('fixed_resource.obd_menu')}`;
        const fileName = getMeasurementFileName(dataName ?? '', functionName, projectDetails.information.vin ?? '');

        csvWriter = new CsvFileWriter(fileName);
        await csvWriter.init();

        const exportCsvParams = {
          csvFileWriter: csvWriter,
          currentComponent: this,
          dataMonitorComponent: this.$refs.data_monitor,
          projectDetails,
          measurementId,
          ecuName,
          errorDialogCallback: () => { },
          updateExportProgress: this.updateExportProgress,
        };
        await exportCsv(exportCsvParams);
      } catch (error) {
        if (CsvFileWriter.isUserCancel(error)) {
          // ユーザーがCSVの書き込みをキャンセルしたら何もせず終了
          return;
        }
        alert.showError(this, null, this.$t('message.common_systemError'), () => { });
      } finally {
        this.showCsvProgress = false;
        csvWriter && await csvWriter.close();
      }
    }
  },

  created() {
    // noop
  },

  mounted() {
    this.mountedLogic();
  },
};
